<template>
  <div class="freight-tables-table">
    <BaseDataTable
      :columns="columns"
      :items="items"
      :row-key="rowKey"
      :loading="loading"
      :pagination="pagination"
      :scroll="scroll"
      :selectable="selectable"
      :selected-row-keys="selectedRowKeys"
      @change="onTableChange"
      @selection-change="$emit('selection-change', $event)"
    >
      <template slot="tipo" slot-scope="text, record">
        <FreightTableTag v-if="record.tipo" :variant="tipoVariant(record)">
          {{ record.tipo }}
        </FreightTableTag>
        <span v-else>—</span>
      </template>

      <template slot="calculo" slot-scope="text, record">
        <FreightTableTag v-if="record.calculo" :variant="calculoVariant(record)">
          {{ record.calculo }}
        </FreightTableTag>
        <span v-else>—</span>
      </template>

      <template slot="fornecedor" slot-scope="text, record">
        <div v-if="record.fornecedorNome" class="freight-tables-table__supplier">
          <span
            class="freight-tables-table__supplier-name"
            :title="record.fornecedorNome"
          >
            {{ record.fornecedorNome }}
          </span>
          <span class="freight-tables-table__supplier-doc">
            {{ record.fornecedorDocumento }}
          </span>
        </div>
        <span v-else>—</span>
      </template>

      <template slot="status" slot-scope="text, record">
        <FreightTableTag v-if="record.isDraft" variant="warning">
          Rascunho
        </FreightTableTag>
        <StatusBadge
          v-else
          :label="record.status"
          :active="record.statusValor === FREIGHT_TABLE_STATUS.ATIVA"
        />
      </template>

      <template slot="acoes" slot-scope="text, record">
        <div class="freight-tables-table__actions">
          <a
            class="freight-tables-table__link"
            href="#"
            @click.prevent="$emit('view-details', record)"
          >
            {{ record.isDraft ? "Continuar" : "Detalhes" }}
            <svg
              class="freight-tables-table__link-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>

          <a-dropdown
            v-if="hasRowActions || record.isDraft"
            :trigger="['click']"
            placement="bottomRight"
            overlay-class-name="freight-tables-table__actions-menu"
          >
            <a-button
              class="freight-tables-table__more-btn"
              shape="circle"
              size="small"
            >
              <a-icon type="more" />
            </a-button>

            <a-menu
              v-if="record.isDraft"
              slot="overlay"
              @click="(info) => onActionSelect(info.key, record)"
            >
              <a-menu-item
                key="discard-draft"
                class="freight-tables-table__action--danger"
              >
                <a-icon type="delete" /> Descartar
              </a-menu-item>
            </a-menu>

            <a-menu v-else slot="overlay" @click="(info) => onActionSelect(info.key, record)">
              <a-menu-item v-if="canAddRoute" key="add-route">
                <a-icon type="environment" /> Adicionar rota
              </a-menu-item>

              <a-menu-item v-if="canAddCarrier" key="add-carrier">
                <a-icon type="car" /> Adicionar transportadora
              </a-menu-item>

              <a-menu-item v-if="canApprove" key="approve-validity">
                <a-icon type="calendar" /> Aprovar vigência
              </a-menu-item>

              <a-menu-item v-if="canExport" key="export-csv">
                <a-icon type="file-text" /> Exportar csv
              </a-menu-item>

              <a-menu-item
                v-if="canCancel"
                key="cancel-validity"
                class="freight-tables-table__action--danger"
              >
                <a-icon type="close-circle" /> Cancelar vigência
              </a-menu-item>
            </a-menu>
          </a-dropdown>
        </div>
      </template>
    </BaseDataTable>
  </div>
</template>

<script>
import BaseDataTable from "@/shared/components/BaseDataTable.vue";
import StatusBadge from "@/shared/components/StatusBadge.vue";
import FreightTableTag from "./FreightTableTag.vue";
import { FREIGHT_TABLE_STATUS } from "../../constants/freightTableStatus";
import {
  FREIGHT_TABLE_TIPO_VARIANT,
  FREIGHT_TABLE_CALCULO_VARIANT,
} from "../../constants/freightTableOptions";

const SORT_FIELDS = {
  codigo: "cd",
  tipo: "tp_receita_despesa",
};

export default {
  name: "FreightTablesTable",
  components: { BaseDataTable, StatusBadge, FreightTableTag },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    rowKey: {
      type: String,
      default: "id",
    },
    selectedRowKeys: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      default: () => ({ current: 1, perPage: 10, total: 0 }),
    },
    canAddRoute: {
      type: Boolean,
      default: true,
    },
    canAddCarrier: {
      type: Boolean,
      default: true,
    },
    canApprove: {
      type: Boolean,
      default: true,
    },
    canCancel: {
      type: Boolean,
      default: true,
    },
    canExport: {
      type: Boolean,
      default: true,
    },
    selectable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      FREIGHT_TABLE_STATUS,
      scroll: { x: 1282 },
      columns: [
        {
          title: "Código",
          dataIndex: "codigo",
          key: "codigo",
          width: 108,
          sorter: true,
        },
        {
          title: "Descrição",
          dataIndex: "descricao",
          key: "descricao",
          width: 232,
        },
        {
          title: "Tipo",
          dataIndex: "tipo",
          key: "tipo",
          width: 117,
          sorter: true,
          scopedSlots: { customRender: "tipo" },
        },
        {
          title: "Tabela",
          dataIndex: "tabela",
          key: "tabela",
          width: 128,
        },
        {
          title: "Cálculo",
          dataIndex: "calculo",
          key: "calculo",
          width: 170,
          scopedSlots: { customRender: "calculo" },
        },
        {
          title: "Fornecedor",
          key: "fornecedor",
          width: 214,
          scopedSlots: { customRender: "fornecedor" },
        },
        {
          title: "Status",
          key: "status",
          width: 119,
          scopedSlots: { customRender: "status" },
        },
        {
          title: "Ações",
          key: "acoes",
          width: 146,
          scopedSlots: { customRender: "acoes" },
        },
      ],
    };
  },
  computed: {
    hasRowActions() {
      return (
        this.canAddRoute ||
        this.canAddCarrier ||
        this.canApprove ||
        this.canCancel ||
        this.canExport
      );
    },
  },
  methods: {
    onTableChange({ page, perPage, sorter }) {
      this.$emit("table-change", {
        page,
        perPage,
        sort: this.buildSort(sorter),
      });
    },
    buildSort(sorter) {
      if (!sorter) return null;
      const field = SORT_FIELDS[sorter.field] || sorter.field;
      return `${field}|${sorter.direction}`;
    },
    tipoVariant(record) {
      return FREIGHT_TABLE_TIPO_VARIANT[Number(record.tipoValor)] || "neutral";
    },
    calculoVariant(record) {
      return (
        FREIGHT_TABLE_CALCULO_VARIANT[Number(record.calculoValor)] || "neutral"
      );
    },
    onActionSelect(key, record) {
      this.$emit("action", { key, record });
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.freight-tables-table {
  &__supplier {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__supplier-name {
    color: @color-text-dark;
    font-weight: 600;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__supplier-doc {
    font-size: 12px;
    color: @color-text-medium;
    margin-top: 2px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: @color-primary;
    font-weight: 500;

    &:hover,
    &:focus {
      color: @color-primary-hover;
      text-decoration: none;
    }
  }

  &__link-icon {
    width: 16px;
    height: 16px;
  }

  &__more-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    min-width: 24px;
    padding: 0;
    color: @color-primary;
    border-color: @color-primary;

    &:hover,
    &:focus {
      color: @color-primary-hover;
      border-color: @color-primary-hover;
      background: transparent;
    }
  }
}
</style>

<style lang="less">
@import "~@/shared/styles/variables.less";

.freight-tables-table__actions-menu {
  .ant-dropdown-menu {
    padding: 4px 0;
    border-radius: 6px;
  }

  .ant-dropdown-menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    font-size: 14px;
    color: @color-text-dark;

    .anticon {
      color: @color-text-medium;
    }
  }

  .freight-tables-table__action--danger {
    color: @color-danger;

    .anticon {
      color: @color-danger;
    }
  }
}
</style>
